import { Autocomplete, Box, Stack, TextField, Tooltip } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import cookie from "react-cookies";
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import service, { basicUrl } from "../api";
import { UAM_PREFIX } from '../api/admin';
import { EXPORT_AUDIT_LOG_URL, EXPORT_NEW_AUDIT_LOG_URL } from '../api/audit';
import { SYSLIB_PREFIX } from '../api/systemLibrary';
import { fetchTenantPageList, getTenantInfo } from '../api/tenant';
import eventBus from '../common/eventBus';
import { isBridgeLoginHost, logoutFunction, persistCurrentTenantCustomizedDomain, redirectToLogin, removeAllCookiesAndLoginDomainInRootDomain } from '../common/utils';
import { COOKIE_SETUP, MULTI_TENANT, RIMS_COOKIE_STG_CONSTANTS, RIMS_LOCAL_STG_CONSTANTS, RIMS_SESSION_STG_CONSTANTS, ROLE_TYPE_ENUM, RTQ_EVENTS } from '../constants';
import { useTenant } from '../hook/tenantConfig';
import { useCookieData } from '../hook/useCookieData';
import { withAuth } from './AuthWrapper';
import ModuleNavLauncher from './ModuleNavLauncher';
import Avatar from './Avatar';
import ChangePswDialog from "./ChangePswDialog";
import Loading from './Loading';
import SystemSettings from './SystemSettings';
import axios from 'axios';
import { clearModuleListPaginationMemory } from '../common/moduleListPaginationMemory';
import { resolveTenantSwitchReturnPath } from '../common/acModuleHomePath';
import { isMissingTenantId, shouldSkipTenantSwitch } from '../common/tenantSwitch';
import { useConfigStore } from '../store/config';

function AppHeader(props: { brandingVersion?: number; authList?: string[] }) {
  const { t } = useTranslation();
  const { brandingVersion = 0 } = props;
  const username = "";
  const { pathname } = useLocation();
  const [tenantList, setTenantList] = useState<any>([]);
  const [currentTenant, setCurrentTenant] = useState<any>();
  const matchedAcMenus = useConfigStore((state) => state.matchedAcMenus);
  const { userInfo: cookieUserInfo } = useCookieData();
  const { tenantInfo } = useTenant(brandingVersion);
  let userInfo = {} as any;
  try {
    userInfo = cookieUserInfo ? JSON.parse(decodeURIComponent(atob(cookieUserInfo))) : {};
  } catch(error) {
    console.log(error)
  }
  const showTenantSelect = isBridgeLoginHost()
    && sessionStorage.getItem(RIMS_SESSION_STG_CONSTANTS.IS_FREYR_TENANT) === "true"
    && (userInfo.roleTypeList?.includes(ROLE_TYPE_ENUM.SUPER_ADMIN) || userInfo.roleTypeList?.includes(ROLE_TYPE_ENUM.ADMIN_CENTER_ADMIN))

  const syncTenantCustomizedDomain = async (tenantId: string) => {
    if (isMissingTenantId(tenantId)) {
      persistCurrentTenantCustomizedDomain("");
      return;
    }
    setLoading(true);
    try {
      const res: any = await getTenantInfo(tenantId);
      persistCurrentTenantCustomizedDomain(res?.data?.customizedDomain);
    } catch {
      persistCurrentTenantCustomizedDomain("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetch Tenant List
    if (showTenantSelect) {
      fetchTenantPageList().then(
        (data: any) => {
          setTenantList(data.data);
          let currentId = ""
          const sessionCurrentId = sessionStorage.getItem(RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_ID)
          const cookieCurrentId = cookie.load(RIMS_COOKIE_STG_CONSTANTS.AC_TENANT_ID)
          if (sessionCurrentId) {
            currentId = sessionCurrentId
          } else if (cookieCurrentId) {
            currentId = cookieCurrentId
            sessionStorage.setItem(RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_ID, currentId)
          } else {
            if (userInfo.tenantId == MULTI_TENANT) {
              currentId = cookie.load(RIMS_COOKIE_STG_CONSTANTS.USER_TENANT_ID)
              sessionStorage.setItem(RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_ID, currentId)
            } else {
              currentId = userInfo.tenantId;
              sessionStorage.setItem(RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_ID, currentId)
            }
          }
          setCurrentTenant(data.data.find((item: any) => {
            return item.id == currentId;
          }));
          void syncTenantCustomizedDomain(String(currentId || ""));
        }).catch()
    }
  }, [])

  useEffect(() => {
    // judgeUserHasESign()


    eventBus.addListener(RTQ_EVENTS.COMMON.EXPORT_FILE_START, exportFileStart)
    eventBus.addListener(RTQ_EVENTS.COMMON.EXPORT_FILE_END, exportFileEnd)

    eventBus.addListener(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_START, downloadTemStart)
    eventBus.addListener(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_END, downloadTemEnd)
    return () => {
      eventBus.removeListener(RTQ_EVENTS.COMMON.EXPORT_FILE_START, exportFileStart)
      eventBus.removeListener(RTQ_EVENTS.COMMON.EXPORT_FILE_END, exportFileEnd)

      eventBus.removeListener(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_START, downloadTemStart)
      eventBus.removeListener(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_END, downloadTemEnd)
    }
  }, []);

  const exportFileStart = (func: any) => {
    if (func.api === EXPORT_AUDIT_LOG_URL || func.api === EXPORT_NEW_AUDIT_LOG_URL) {
      setLoadingWord(t("common.loading.exportDataTip"))
    } else {
      setLoadingWord(t("common.loading.exportData"))
    }
    setLoading(true);
    if (func.api) {
      readyToPolling(func.api, func.data, func.enqueueSnackbar)
    } else {
      func();
    }
  }

  const exportFileEnd = (func: any) => {
    setLoading(false);
    setLoadingWord('')
  }

  const downloadTemStart = (func: any) => {
    setLoadingWord(t("common.loading.downloadTemplate"))
    setLoading(true);
    if (func.api) {
      let timeZone = -new Date().getTimezoneOffset() / 60
      if (func.api.indexOf(`${UAM_PREFIX}`) > 0 || func.api.indexOf(`${SYSLIB_PREFIX}`) > 0) {
        downloadFileCommon(func.api, func.data, func.enqueueSnackbar, 'download template')
      } else {
        const downloadData = {
          ...func.data,
          "timeZone": timeZone
        }
        downloadFileCommon(func.api, downloadData, func.enqueueSnackbar, 'download template')
      }
    } else {
      func();
    }
  }

  const downloadTemEnd = (func: any) => {
    setLoading(false);
  }

  const readyToPolling = (api: string, data: any, enqueueSnackbar: any) => {
    let timeZone = -new Date().getTimezoneOffset() / 60
    // if (api.indexOf(`${UAM_PREFIX}`) > -1 || api.indexOf(`${SYSLIB_PREFIX}`) > -1) {
    //   downloadFileCommon(`${api}&timeZone=${timeZone}`, {}, enqueueSnackbar)
    // } else {
    const downloadData = {
      ...data,
      "timeZone": timeZone
    }
    downloadFileCommon(api, downloadData, enqueueSnackbar)
    // }
  }

  const downloadFileCommon = (url: string, data = {}, enqueueSnackbar: any, type = '') => {
    const dataLength = Object.keys(data).length
    let token = window.localStorage.getItem(RIMS_LOCAL_STG_CONSTANTS.LOGIN_TOKEN)
    // let token = 'eyJraWQiOiIzelV4eHVkNEx4NElEakFhdmhhcUlBQzJIR0pEaHRoajZvM1IzVUs3NlQ0PSIsImFsZyI6IlJTMjU2In0.eyJzdWIiOiIzZTE4OWY3Mi1hMzlhLTQ2NjItODNiYS0zNmQ3OTBkZDMwMzMiLCJpc3MiOiJodHRwczpcL1wvY29nbml0by1pZHAudXMtZWFzdC0xLmFtYXpvbmF3cy5jb21cL3VzLWVhc3QtMV9rM0E0UkZyVGUiLCJjbGllbnRfaWQiOiI3NmowMDdtOG02MnZ2ZXQ2amVjODNkMnVkIiwib3JpZ2luX2p0aSI6ImU2N2MwYmQ5LTUyMmMtNDUwNC04MzczLWJhN2QwYmVkOWFmNCIsImV2ZW50X2lkIjoiOTU5MmZjM2EtZGFmNy00MGUxLWIwYWMtMTYyMjhjNTc0N2EzIiwidG9rZW5fdXNlIjoiYWNjZXNzIiwic2NvcGUiOiJhd3MuY29nbml0by5zaWduaW4udXNlci5hZG1pbiIsImF1dGhfdGltZSI6MTY5MzM2NzQzOSwiZXhwIjoxNjkzNDUzODM5LCJpYXQiOjE2OTMzNjc0MzksImp0aSI6IjI3MmZmM2E5LWE3MWMtNDA2ZS1hMzE3LWE1Y2VmZGVmYTczMiIsInVzZXJuYW1lIjoiYWRtaW4ifQ.Akt86L9cQdhIyfel3y5TtEqpdb9AXsChu0WmGRd3aSxuEkpxkoWPd6ujr_CRd_kf5piipFbdzxbj2UNTIX67uTMF07PVx1pS3scoOIifBb2TD8xvybWDwnK8ycIvR77Vd_kpeoYlOQKJ-I069UapzX7V4wNzRC_pcI2txKcsxPHVedcEPSQ5G4-ustpmqnr4NQggMD3Owmshxz5bBHgqKL6r7f6h9sm_Gjl65bCeX7BgpLP8kQw2rd_rRTcPeTF4FbBFYgZeMf8kiMyg2XLdqoPa51rKhdwsavbMICw4vcjLkTB-bSv80rsT3yL5BJo8KOkDE0_k7X0OuGvBtXO7Ng'
    let executePolling = setTimeout(
      () => {
        service({
          method: dataLength ? 'post' : 'get',
          url: url,
          responseType: 'arraybuffer',
          data: data
        }).then((res: any) => {
          if (res.headers['export-status'] === '3' || type === 'download template') {
            eventBus.emit(RTQ_EVENTS.COMMON.EXPORT_FILE_END)
            eventBus.emit(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_END) // download template file
            clearTimeout(executePolling);
            let fileName = res.headers['content-disposition'].split(';')[1].split('filename=')[1];
            if (!res.data) { return }
            let url = window.URL.createObjectURL(new Blob([res.data]));
            enqueueSnackbar?.("The file has been downloaded successfully.", { variant: "success", anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            let element = document.createElement('a');
            element.style.display = 'none';
            element.href = url;
            element.setAttribute('download', decodeURIComponent(fileName));
            document.body.appendChild(element);
            element.click();
          } else {
            downloadFileCommon(url, data, enqueueSnackbar);
          }
        }).catch((err: any) => {
          clearTimeout(executePolling);
          eventBus.emit(RTQ_EVENTS.COMMON.EXPORT_FILE_END)
          eventBus.emit(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_END) // download template file
          if (err.response.status === 401) {
            window.location.href = '/';
            logoutFunction()
          }
          if (enqueueSnackbar) {
            const unit8Msg: any = new Uint8Array(err.response.data)
            const error_msg = JSON.parse(String.fromCharCode.apply(null, unit8Msg))
            enqueueSnackbar(error_msg.message, { variant: "error", anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            // enqueueSnackbar('Failed to download the PDF, Please try again later', { variant: "error", anchorOrigin: { vertical: 'top', horizontal: 'center' } });
          }
        })
      }, 2000)

  }

  const [loading, setLoading] = useState(false);
  const [loadingWord, setLoadingWord] = useState('');

  const [changePswVisible, setChangePswVisible] = useState(false);

  const linkTo = useNavigate();

  const findParentNode = (e: any): any => {
    const parentElement = e.parentElement || e.parentNode
    if (parentElement) {
      if (parentElement.id === 'layout-user-dropdown-target') {
        return true
      } else {
        return findParentNode(parentElement)
      }
    } else {
      return false
    }
  }

  const handleClickLogout = () => {
    const token = cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_TOKEN);
    const refreshToken = cookie.load(RIMS_COOKIE_STG_CONSTANTS.REFRESH_TOKEN);
    axios({
      method: "delete",
      baseURL: basicUrl,
      url: '/common-auth/logout',
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        Authorization: `Bearer ${token}`,
        'refresh-token': refreshToken
      },
    })
      .then((data: any) => {
      }).catch((err) => {
        console.log(err);
      }).finally(() => {
        removeAllCookiesAndLoginDomainInRootDomain();
        redirectToLogin();
      })
  }

  const saveChangePwd = () => {
    setChangePswVisible(false)
  }

  const handleChangeTenant = async (val: any) => {
    if (shouldSkipTenantSwitch(val?.id, currentTenant?.id)) {
      return;
    }
    setCurrentTenant(val);
    cookie.save(RIMS_COOKIE_STG_CONSTANTS.AC_TENANT_ID, val.id, COOKIE_SETUP)
    sessionStorage.setItem(RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_ID, String(val.id));
    await syncTenantCustomizedDomain(String(val.id));
    clearModuleListPaginationMemory();
    const returnTo = resolveTenantSwitchReturnPath({
      currentPathname: pathname,
      matchedAcMenus,
    });
    // Same-path navigate must still notify subscribers so CommonIframe rebuilds URL.
    linkTo(returnTo, {
      replace: true,
      state: { acTenantId: String(val.id) },
    });
  }

  return (
    <>
      <header className="header">
        <div style={{ height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="logo-space">
          <Stack
            gap={2}
            direction="row"
            alignItems="center"
            className="freyafusion-logo-space"
          >
            <ModuleNavLauncher />
            <img style={{verticalAlign: 'middle'}} alt="" src={tenantInfo.logo} />
            <h4>{t('common.ac')}</h4>
          </Stack>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            {showTenantSelect && <><span>{t('common.tenant')} </span>
              <Box component="form" className="form-group">
                <Autocomplete
                  options={tenantList}
                  value={currentTenant?.name ? currentTenant : null}
                  sx={{
                    '& .MuiOutlinedInput-root': { padding: 0, border: 1, borderColor: '#ced4da' },
                    '& .MuiTextField-root': { marginLeft: 1, marginBottom: 0 },
                    '& .MuiOutlinedInput-root .MuiAutocomplete-input': { border: 'none', paddingLeft: '8px !important' },
                    '& .MuiFormControl-root .MuiInputBase-root input:focus': { outline: 'none !important' },
                    '& .MuiAutocomplete-inputRoot': { paddingLeft: '10px', minWidth: '200px' },
                    // '& .MuiAutocomplete-endAdornment': { top: 'calc(50% - 12px)' }
                  }}
                  disableClearable
                  style={{ width: '100%' }}
                  className='custom-autocomplete'
                  onChange={(event: any, value: any) => {
                    handleChangeTenant(value)
                  }}
                  renderOption={(props, option: any, { selected }) => (
                    <li {...props} key={option.id}> {option.name} </li>
                  )}
                  getOptionLabel={(option: any) => option.name || ""}
                  isOptionEqualToValue={(option: any, value) => option.id === value.id}
                  renderInput={(params: any) => <TextField {...params} inputProps={{ ...params.inputProps, onKeyDown: (e) => { if (e.keyCode === 13) { e.preventDefault(); } }, autoComplete: 'new-password', }} />}
                />
              </Box></>}
            <Tooltip title={t("systemSettings.title")}>
              <div className='m-l-10'><SystemSettings></SystemSettings></div>
            </Tooltip>
            <Avatar onLogout={handleClickLogout}></Avatar>
          </div>
        </div >
        <ChangePswDialog
          visible={changePswVisible}
          cancel={() => { setChangePswVisible(false) }}
          submit={saveChangePwd}
          username={username}
          disabledSubmit={false}
          open={() => { }}>
        </ChangePswDialog>
        {/* <CommonDialog openDialog={openDialog} title={'Unsaved Changes'} content={'Are you sure you want to leave this page? Any unsaved changes will be lost.'} isDeleteDialog={false} handlePrimaryBtn={() => handleClickYesBtn()} handleSecondaryBtn={() => setOpenDialog(false)} primaryBtnName={'Yes'} secondaryBtnName={'No'}>
        </CommonDialog> */}
      </header >
      <Loading open={loading} wording={loadingWord} />
    </ >
  );
}
export default memo(withAuth(AppHeader));
